import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import axios from 'axios'

// Theme configuration interface
export interface ThemeConfig {
  // Branding texts
  companyName: string
  appName: string
  tagline: string
  loginMessage: string
  copyright: string

  // Logo URLs (null = use default)
  logoUrl: string | null
  faviconUrl: string | null

  // Colors
  colors: {
    primary: string
    secondary: string
    accent: string
    sidebarBg: string
    sidebarText: string
    companyColorSidebar: string
    pageBg: string
    cardBg: string
    cardText: string
    loginBg: string
    loginCardBg: string
    loginText: string
    companyColorLogin: string
  }
}

// Default theme - MUST match current application appearance EXACTLY
const DEFAULT_THEME: ThemeConfig = {
  companyName: 'CONQUIST',
  appName: 'GrowUp!',
  tagline: 'Community Platform',
  loginMessage: 'Inserisci le tue credenziali per accedere',
  copyright: 'Conquist s.r.l.',
  logoUrl: null,
  faviconUrl: null,
  colors: {
    primary: '#f97316',      // Tailwind orange-500
    secondary: '#ea580c',    // Tailwind orange-600
    accent: '#dc2626',       // Tailwind red-600
    sidebarBg: '#475569',    // Tailwind slate-600
    sidebarText: '#ffffff',  // white
    companyColorSidebar: '#7dd3fc',  // Tailwind sky-300 - colore nome azienda sidebar
    pageBg: '#f1f5f9',       // Tailwind slate-100
    cardBg: '#ffffff',       // white
    cardText: '#111827',     // Tailwind gray-900
    loginBg: '#f1f5f9',      // Tailwind slate-100
    loginCardBg: '#ffffff',  // white
    loginText: '#111827',    // Tailwind gray-900
    companyColorLogin: '#0f172a',  // Tailwind slate-900 - colore nome azienda footer login
  },
}

interface ThemeContextType {
  theme: ThemeConfig
  loading: boolean
  error: string | null
  refreshTheme: () => Promise<void>
}

const ThemeContext = createContext<ThemeContextType>({
  theme: DEFAULT_THEME,
  loading: true,
  error: null,
  refreshTheme: async () => {},
})

// Helper to convert hex to RGB
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (result) {
    return `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
  }
  return '0 0 0'
}

// Apply theme colors as CSS variables
function applyThemeColors(colors: ThemeConfig['colors']) {
  const root = document.documentElement

  // Set CSS variables for colors
  root.style.setProperty('--color-primary', colors.primary)
  root.style.setProperty('--color-primary-rgb', hexToRgb(colors.primary))
  root.style.setProperty('--color-secondary', colors.secondary)
  root.style.setProperty('--color-secondary-rgb', hexToRgb(colors.secondary))
  root.style.setProperty('--color-accent', colors.accent)
  root.style.setProperty('--color-accent-rgb', hexToRgb(colors.accent))
  root.style.setProperty('--color-sidebar-bg', colors.sidebarBg)
  root.style.setProperty('--color-sidebar-bg-rgb', hexToRgb(colors.sidebarBg))
  root.style.setProperty('--color-sidebar-text', colors.sidebarText)
  root.style.setProperty('--color-page-bg', colors.pageBg)
  root.style.setProperty('--color-card-bg', colors.cardBg)
  root.style.setProperty('--color-card-text', colors.cardText)
  root.style.setProperty('--color-login-bg', colors.loginBg)
  root.style.setProperty('--color-login-card-bg', colors.loginCardBg)
  root.style.setProperty('--color-login-text', colors.loginText)
}

// Update document title with company and app name
function updateDocumentTitle(companyName: string, appName: string) {
  document.title = `${companyName} ${appName}`
}

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<ThemeConfig>(DEFAULT_THEME)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchTheme = async () => {
    try {
      // Use base URL from environment or fallback
      // VITE_API_URL già include /api/v1
      const baseUrl = import.meta.env.VITE_API_URL || '/api/v1'
      const response = await axios.get(`${baseUrl}/theme/config/`)

      const data = response.data
      const newTheme: ThemeConfig = {
        companyName: data.company_name || DEFAULT_THEME.companyName,
        appName: data.app_name || DEFAULT_THEME.appName,
        tagline: data.tagline || DEFAULT_THEME.tagline,
        loginMessage: data.login_message || DEFAULT_THEME.loginMessage,
        copyright: data.copyright || DEFAULT_THEME.copyright,
        logoUrl: data.logo_url || null,
        faviconUrl: data.favicon_url || null,
        colors: {
          primary: data.colors?.primary || DEFAULT_THEME.colors.primary,
          secondary: data.colors?.secondary || DEFAULT_THEME.colors.secondary,
          accent: data.colors?.accent || DEFAULT_THEME.colors.accent,
          sidebarBg: data.colors?.sidebar_bg || DEFAULT_THEME.colors.sidebarBg,
          sidebarText: data.colors?.sidebar_text || DEFAULT_THEME.colors.sidebarText,
          companyColorSidebar: data.colors?.company_color_sidebar || DEFAULT_THEME.colors.companyColorSidebar,
          pageBg: data.colors?.page_bg || DEFAULT_THEME.colors.pageBg,
          cardBg: data.colors?.card_bg || DEFAULT_THEME.colors.cardBg,
          cardText: data.colors?.card_text || DEFAULT_THEME.colors.cardText,
          loginBg: data.colors?.login_bg || DEFAULT_THEME.colors.loginBg,
          loginCardBg: data.colors?.login_card_bg || DEFAULT_THEME.colors.loginCardBg,
          loginText: data.colors?.login_text || DEFAULT_THEME.colors.loginText,
          companyColorLogin: data.colors?.company_color_login || DEFAULT_THEME.colors.companyColorLogin,
        },
      }

      setTheme(newTheme)
      applyThemeColors(newTheme.colors)
      updateDocumentTitle(newTheme.companyName, newTheme.appName)
      setError(null)
    } catch (err) {
      console.warn('Failed to fetch theme config, using defaults:', err)
      // Use defaults on error - app will still work
      applyThemeColors(DEFAULT_THEME.colors)
      updateDocumentTitle(DEFAULT_THEME.companyName, DEFAULT_THEME.appName)
      setError('Impossibile caricare il tema')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Apply default colors immediately
    applyThemeColors(DEFAULT_THEME.colors)
    // Then fetch custom theme
    fetchTheme()
  }, [])

  const refreshTheme = async () => {
    setLoading(true)
    await fetchTheme()
  }

  return (
    <ThemeContext.Provider value={{ theme, loading, error, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  return useContext(ThemeContext)
}

export { DEFAULT_THEME }
